import {
  COMPONENT_VALUE_TYPES as types,
  REGIONS,
  TEXT_PROPS_VALIDATION
} from 'shared/constants';
import range from 'lodash/range';

const propsSchema = [
  {
    key: 'region',
    label: 'Region',
    type: types.ENUM,
    options: REGIONS
  },
  {
    key: 'title',
    label: 'Overlay Title',
    type: types.TEXT
  },
  {
    key: 'subtitle',
    label: 'Overlay Subtitle',
    type: types.TEXT
  },
  {
    key: 'placeholderImageUrl',
    label: 'Placeholder Image Url',
    type: types.TEXT
  },
  {
    key: 'scheduledTime',
    label: 'Scheduled Time (for placeholder to disappear, only works for first video in playlist)',
    type: types.DATETIME
  },
  {
    key: 'videoAspectRatio',
    label: 'Video Aspect Ratio',
    type: types.TEXT
  },
  {
    key: 'pipEnabled',
    label: 'Enable Picture in Picture',
    type: types.BOOLEAN
  },
  {
    key: 'hideMobilePlaylist',
    label: 'Hide playlist on mobile devices',
    type: types.BOOLEAN
  },
  {
    key: 'options',
    label: 'Player Options',
    type: types.OBJECT,
    children: [
      {
        key: 'autoplay',
        label: 'Autoplay (may not work if video is not muted)',
        type: types.BOOLEAN,
      },
      {
        key: 'muted',
        label: 'Muted',
        type: types.BOOLEAN,
      },
      {
        key: 'liveui',
        label: 'Live UI (A progress bar for seeking within the live window)',
        type: types.BOOLEAN,
      },
    ]
  },
  {
    key: 'playlistSettings',
    label: 'Playlist Settings',
    type: types.LIVESTREAM_PLAYLIST,
    children: [
      {
        key: 'polling',
        label: 'Polling (Realtime Update Playlist)',
        type: types.OBJECT,
        children: [
          {
            key: 'enabled',
            label: 'Should only use for livestream, changing playlist for regular videos will be very abrupt for users',
            type: types.BOOLEAN,
          },
          {
            key: 'refreshInterval',
            label: 'Refresh Interval (Seconds)',
            type: types.ENUM,
            options: range(0, 61, 1)
          },
          {
            key: 'path',
            label: 'Path (Format: new-path-to-playlist)',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'playlist',
        label: 'Playlist',
        type: types.ARRAY,
        children: [
          {
            key: 'source',
            label: 'Video Source',
            type: types.TEXT,
            options: {
              validation: TEXT_PROPS_VALIDATION.LIVESTREAM_VIDEO_PLAYER
            }
          },
          {
            key: 'title',
            label: 'Video title',
            type: types.TEXT
          },
          {
            key: 'subtitle',
            label: 'Video subtitle',
            type: types.TEXT
          },
          {
            key: 'poster',
            label: 'Cover Image',
            type: types.TEXT
          },
          {
            key: 'shareId',
            label: 'Share ID',
            tooltip: 'For instructions on how to use the Share ID, go to how to share a video in the livestream.',
            type: types.TEXT
          },
        ]
      }
    ]
  },
  {
    key: 'howToShareVideo',
    label: 'How to share a video in the livestream',
    type: types.OBJECT,
    children: [
      {
        key: 'sectionTag',
        label: 'Section Tag (Used to automatically scroll down to this livestream video player)',
        type: types.TEXT,
      },
      {
        key: 'howTo',
        label: 'Instructions:',
        tooltip: 'If there are no instructions, please contact the FF website development team',
        type: types.TINYMCE,
        options: {
          height: 600,
          menubar: false,
          toolbar: false,
        },
      },
    ]
  }
];

const defaultProps = {
  region: 'US',
  title: 'Stay tuned for a LIVE event!',
  subtitle: 'Event starts 2/16/2020',
  placeholderImageUrl: 'https://genesis-cdn.ff.com/faraday-future-ff-91/faraday-future-ff-91-eminence-hero-image.jpg',
  scheduledTime: { date: null, timezone: null },
  videoAspectRatio: '16/9',
  pipEnabled: true,
  hideMobilePlaylist: false,
  options: {
    autoplay: true,
    muted: true,
    liveui: false,
  },
  playlistSettings: {
    polling: {
      enabled: false,
      refreshInterval: '5',
      path: 'default'
    },
    playlist: [
      {
        source: 'https://cph-p2p-msl.akamaized.net/hls/live/2000341/test/master.m3u8',
        title: 'Previous Stream/Video',
        subtitle: '2:30min | 2-16-2022',
        poster: 'https://genesis-cdn.ff.com/faraday-future-ff-91/faraday-future-ff-91-eminence-hero-image.jpg',
      },
      {
        source: 'http://media.w3.org/2010/05/sintel/trailer.mp4',
        title: 'Previous Stream/Video',
        subtitle: '2:30min | 2-16-2022',
        poster: 'http://media.w3.org/2010/05/sintel/poster.png',
      },
      {
        source: 'http://vjs.zencdn.net/v/oceans.mp4',
        title: 'Previous Stream/Video',
        subtitle: '2:30min | 2-16-2022',
        poster: 'https://genesis-cdn.ff.com/faraday-future-ff-91/faraday-future-ff-91-eminence-hero-image.jpg',
      },
      {
        source: 'http://media.w3.org/2010/05/bunny/movie.mp4',
        title: 'Previous Stream/Video',
        subtitle: '2:30min | 2-16-2022',
        poster: 'http://media.w3.org/2010/05/bunny/poster.png',
      },
      {
        source: 'https://www.youtube.com/watch?v=5YIVBo2ZcIA&list=PLOyDBFacyEtr6LRUcXXPjNazRm_WgryIQ',
        title: 'Youtube Playlist',
        subtitle: 'youtube',
        poster: 'https://genesis-cdn.ff.com/faraday-future-ff-91/faraday-future-ff-91-eminence-hero-image.jpg',
      },
    ]
  },
  howToShareVideo: {
    sectionTag: '',
    howTo: `
      <h1>How to create a link to share a video in the playlist</h1>
      <ol>
        <li>In the Playlist Settings, find the video you want to share and use the share ID. If the share ID is empty, add a unique value.</li>
        <li>Go to the URL in the browser and add this to the end of the URL: ?share=Insert-share-id-here</li>
        <li>Add the share ID where it says "Insert-share-id-here"</li>
        <li>The end of the URL should look something like this: https://genesis.ffau.to/admin/page/63069be203ce450012e99027?share=Youtube Share Playlist (<-- Do not share this link)</li>
        <li>To make the page automatically scroll to the livestream video player, add text to the section tag above</li>
        <li>Add the section tag to the end of the URL with a hashtag. The end of the URL should look something like this: https://genesis.ffau.to/admin/page/63069be203ce150012e99027?share=Youtube Share Playlist#livestream (<-- Do not share this link)</li>
        <li>Save the page and refresh the page with the share URL and make sure the correct video starts playing</li>
        <li>If it works, save and publish the page</li>
        <li>After publishing the page, visit the page and update the end of the URL with the share ID. It should look something like this: <b>https://www.ff.com/us/91launch/?share=Youtube Share Playlist#livestream</b> (<-- Share link should look similar to this)</li>
        <li>If you share that URL, the page scrolls to the livestream video player, and the video in the URL starts playing</li>
      </ol>
    `,
  }
};

export default {
  title: 'Livestream Video Player',
  id: 'LivestreamVideoPlayer',
  propsSchema,
  defaultProps
};
